import React, { useState } from 'react';

import Paragraph from 'components/text/Paragraph';
import PropTypes from 'prop-types';
import colors from 'config/theme/colors';
import styled from 'libs/styled';
import transitions from 'config/theme/transitions';

const Wrapper = styled('div')`
    position: relative;
`;

const Label = styled('label', { shouldForwardProp: prop => ['required'].indexOf(prop) === -1 })`
    position: absolute;
    color: ${colors.grey6};
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    transition: all ${transitions.secondaryFast};
    cursor: text;
    user-select: none;

    ${({ required }) =>
        required &&
        `
        ::after {
            content: '*';
            display: block;
            position: absolute;
            top: 0;
            right: -10px;
        }
    `}
`;

const StyledInput = styled('input', { shouldForwardProp: prop => !['hasInput'].includes(prop) })`
    width: 100%;
    padding-bottom: 8px;
    text-decoration: none;
    border: none;
    border-radius: 0;
    font-size: 16px;
    border-bottom: 1px solid ${colors.grey3};
    background-color: transparent;
    -webkit-appearance: none;

    :focus {
        outline: none;
        & + label {
            top: -8px;
            font-size: 12px;

            ::after {
                right: -8px;
            }
        }
    }

    ${({ hasInput }) =>
        hasInput &&
        `
        outline: none;
        & + label {
            top: -8px;
            font-size: 12px;

            ::after {
                right: -8px;
            }
        }`}

    ::placeholder {
        color: ${colors.grey6};
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
        box-shadow: 0 0 0 30px ${colors.background} inset !important;
    }
`;

const ErrorMessage = styled(Paragraph)`
    color: ${colors.error};
    font-size: 10px;
    line-height: 10px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 16px;
    margin: 0;
`;

const Input = ({
    error,
    label,
    name,
    required = false,
    type,
    color = 'black',
    defaultValue,
    handleBlur = () => {},
    handleChange = () => {},
}) => {
    defaultValue = defaultValue || undefined;
    const [value, setValue] = useState(defaultValue);

    const change = e => {
        setValue(e.target.value);
        handleChange(e);
    };

    const blur = e => {
        if (!e.target.value) {
            setValue(undefined);
        }
        handleBlur(e);
    };

    return (
        <Wrapper>
            <StyledInput
                color={color}
                id={name}
                name={name}
                type={type}
                aria-label={label}
                required={required}
                hasInput={value !== undefined}
                value={value}
                onChange={change}
                onBlur={blur}
            />
            <Label htmlFor={name} required={required}>
                {label}
            </Label>
            {error && <ErrorMessage>*{error.message}</ErrorMessage>}
        </Wrapper>
    );
};

Input.propTypes = {
    color: PropTypes.string,
    defaultValue: PropTypes.string,
    error: PropTypes.exact({
        message: PropTypes.string,
        name: PropTypes.string,
    }),
    handleBlur: PropTypes.func,
    handleChange: PropTypes.func,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool,
    type: PropTypes.string.isRequired,
};

export default Input;
